import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sun, Coffee, Activity, Brain, AlertCircle, Sparkles, ChevronRight, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface BriefingData {
  greeting: string;
  recoveryScore: number;
  energyForecast: 'high' | 'moderate' | 'low';
  topPriority: string;
  cautions: string[];
  opportunities: string[];
  quickActions: Array<{ label: string; path: string; icon: string }>;
}

export function MorningBriefing() {
  const [briefing, setBriefing] = useState<BriefingData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadBriefing();
  }, []);

  const loadBriefing = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Check if already dismissed today
      const dismissKey = `morning-briefing-dismissed-${format(new Date(), 'yyyy-MM-dd')}`;
      if (localStorage.getItem(dismissKey)) {
        setDismissed(true);
        setIsLoading(false);
        return;
      }

      // Fetch today's data
      const today = format(new Date(), 'yyyy-MM-dd');
      
      const [ilData, mwData, whoopData, checkinData] = await Promise.all([
        supabase.from('il_daily').select('*').eq('user_id', user.id).eq('date', today).maybeSingle(),
        supabase.from('mental_wellness_daily' as any).select('*').eq('user_id', user.id).eq('date', today).maybeSingle(),
        supabase.from('whoop_recovery_data').select('recovery_score, hrv_rmssd').eq('user_id', user.id).gte('recorded_at', today).order('recorded_at', { ascending: false }).limit(1).maybeSingle(),
        supabase.from('daily_checkins').select('*').eq('user_id', user.id).eq('date', today).maybeSingle()
      ]);

      // Generate briefing
      const il = ilData.data;
      const mw = mwData.data as any;
      const whoop = whoopData.data;
      const checkin = checkinData.data;

      const recovery = whoop?.recovery_score ?? 
        (il?.sleep_score ? il.sleep_score * 1.2 : null) ?? 
        60;

      const hour = new Date().getHours();
      const greeting = hour < 12 ? 'Good Morning' : hour < 18 ? 'Good Afternoon' : 'Good Evening';

      let energyForecast: 'high' | 'moderate' | 'low' = 'moderate';
      if (recovery > 75) energyForecast = 'high';
      else if (recovery < 50) energyForecast = 'low';

      const cautions: string[] = [];
      const opportunities: string[] = [];

      // Physical factors
      if (il?.il_score > 60) {
        cautions.push('High inflammation - avoid processed foods and alcohol');
      }
      if (il?.sleep_score < 50) {
        cautions.push('Poor sleep quality - prioritise rest tonight');
      }

      // Mental factors
      if (mw?.mental_wellness_score < 40) {
        cautions.push('Mental wellness needs support - consider therapy session');
      }
      if (mw?.energy_score < 40) {
        cautions.push('Low energy predicted - schedule lighter activities');
      }

      // Opportunities
      if (recovery > 70 && (!mw || mw.mental_wellness_score > 60)) {
        opportunities.push('High recovery + good mood = perfect for challenging activities');
      }
      if (checkin?.sunlight_streak && checkin.sunlight_streak > 3) {
        opportunities.push(`${checkin.sunlight_streak}-day sunlight streak! Keep it going today`);
      }
      if (!il || il.hydration_score > 70) {
        opportunities.push('Great hydration pattern - maintain 8 glasses today');
      }

      // Determine top priority
      let topPriority = 'Complete your daily check-in to unlock personalised insights';
      if (il?.il_score > 60) topPriority = 'Reduce inflammation through whole foods';
      else if (mw && mw.mental_wellness_score < 40) topPriority = 'Mental health support - schedule Therappi® session';
      else if (recovery < 50) topPriority = 'Prioritise recovery - gentle movement only';
      else if (recovery > 75) topPriority = 'High recovery - optimise this window for growth';

      const quickActions = [
        { label: 'Daily Check-In', path: '/daily-check-in', icon: 'CheckSquare' },
        recovery < 50 ? { label: 'Nervous System Reset', path: '/sanctuary', icon: 'Brain' } : null,
        mw && mw.mental_wellness_score < 50 ? { label: 'Talk to Therappi®', path: '/therapy', icon: 'MessageCircle' } : null,
        { label: 'View My Scores', path: '/my-scores', icon: 'Activity' }
      ].filter(Boolean) as Array<{ label: string; path: string; icon: string }>;

      setBriefing({
        greeting,
        recoveryScore: Math.round(recovery),
        energyForecast,
        topPriority,
        cautions: cautions.slice(0, 3),
        opportunities: opportunities.slice(0, 2),
        quickActions: quickActions.slice(0, 3)
      });
    } catch (error) {
      console.error('Error loading briefing:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = () => {
    const dismissKey = `morning-briefing-dismissed-${format(new Date(), 'yyyy-MM-dd')}`;
    localStorage.setItem(dismissKey, 'true');
    setDismissed(true);
  };

  if (isLoading || dismissed || !briefing) return null;

  const energyConfig = {
    high: { color: 'text-accent', bg: 'bg-accent/10', label: 'High Energy Expected' },
    moderate: { color: 'text-primary', bg: 'bg-primary/10', label: 'Moderate Energy' },
    low: { color: 'text-destructive', bg: 'bg-destructive/10', label: 'Conservation Mode' }
  };

  const config = energyConfig[briefing.energyForecast];

  return (
    <>
      <Card className="border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10 relative">
        <Button
          variant="ghost"
          size="sm"
          className="absolute top-2 right-2 h-8 w-8 p-0"
          onClick={handleDismiss}
        >
          <X className="h-4 w-4" />
        </Button>
        
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Sun className="w-5 h-5 text-primary" />
            {briefing.greeting}
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className={`${config.bg} ${config.color} text-base`}>
              <Activity className="w-4 h-4 mr-1" />
              Recovery: {briefing.recoveryScore}%
            </Badge>
            <Badge variant="outline" className={`${config.bg} text-base`}>
              {config.label}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-3">
          {/* Top Priority */}
          <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
            <div className="flex items-start gap-2">
              <Sparkles className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-primary mb-1">Today's Priority</div>
                <div className="text-base font-medium">{briefing.topPriority}</div>
              </div>
            </div>
          </div>

          {/* Cautions */}
          {briefing.cautions.length > 0 && (
            <div className="space-y-1">
              <div className="text-sm font-semibold text-muted-foreground flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                Watch Out For
              </div>
              {briefing.cautions.slice(0, isExpanded ? undefined : 2).map((caution, idx) => (
                <div key={idx} className="text-sm pl-4 py-1 border-l-2 border-destructive/30">
                  {caution}
                </div>
              ))}
            </div>
          )}

          {/* Opportunities */}
          {briefing.opportunities.length > 0 && (
            <div className="space-y-1">
              <div className="text-sm font-semibold text-muted-foreground flex items-center gap-1">
                <Sparkles className="w-4 h-4" />
                Opportunities
              </div>
              {briefing.opportunities.map((opp, idx) => (
                <div key={idx} className="text-sm pl-4 py-1 border-l-2 border-accent/30">
                  {opp}
                </div>
              ))}
            </div>
          )}

          {/* Quick Actions */}
          <div className="flex gap-2">
            {briefing.quickActions.map((action, idx) => (
              <Button
                key={idx}
                variant="outline"
                size="default"
                onClick={() => navigate(action.path)}
                className="flex-1 text-sm"
              >
                {action.label}
              </Button>
            ))}
          </div>

          {/* Expand button */}
          {!isExpanded && (briefing.cautions.length > 2 || briefing.opportunities.length > 2) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(true)}
              className="w-full text-xs"
            >
              See Full Briefing
              <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          )}
        </CardContent>
      </Card>
    </>
  );
}
